import { createSlice } from "@reduxjs/toolkit";

// Permissions are stored as { [key]: ['own', 'assigned'] } — an array of granted scopes.
// Backend (post-multi-scope) emits [{ key, scope, scopes }]. Prefer `scopes`; fall back to
// the legacy `scope` string. A bare string entry (very old payload) is treated as ['all'].
const normalizePermissions = (raw) => {
  if (!Array.isArray(raw)) return {};
  const out = {};
  for (const entry of raw) {
    if (typeof entry === "string") {
      out[entry] = ["all"];
    } else if (entry && typeof entry === "object" && entry.key) {
      if (Array.isArray(entry.scopes) && entry.scopes.length > 0) {
        out[entry.key] = entry.scopes;
      } else {
        out[entry.key] = [entry.scope || "all"];
      }
    }
  }
  return out;
};

const initialState = {
  isLoggedIn: false,
  user: null,
  userProfile: null,
  permissions: {},
  authChecked: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUserProfile: (state, action) => {
      state.userProfile = action.payload;
    },
    login: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload;
      state.permissions = normalizePermissions(action.payload?.permissions);
      state.authChecked = true;
    },
    setPermissions: (state, action) => {
      state.permissions = normalizePermissions(action.payload);
    },
    logout: (state) => {
      state.isLoggedIn = false;
      state.user = null;
      state.userProfile = null;
      state.permissions = {};
      state.authChecked = true;
      localStorage.removeItem("redirectAfterLogin");
    },
    setAuthChecked: (state) => {
      state.authChecked = true;
    },
  },
});

export const selectIsSuperAdmin = (state) =>
  Boolean(state?.user?.user?.is_super_admin) ||
  Number(state?.user?.user?.role?.id) === 1;

export const selectCurrentUserId = (state) =>
  Number(state?.user?.user?.id) || null;

/**
 * Returns the array of granted scopes (e.g. ['own', 'assigned']) for a permission key,
 * or null if not granted. Superadmin returns ['all'] for every key.
 */
export const selectPermissionScopes = (key) => (state) => {
  if (selectIsSuperAdmin(state)) return ["all"];
  const perms = state?.user?.permissions || {};
  if (!Object.prototype.hasOwnProperty.call(perms, key)) return null;
  const v = perms[key];
  if (Array.isArray(v)) return v.length ? v : ["all"];
  // Defensive: handle a stray single-string value that slipped past normalization.
  return [v || "all"];
};

/**
 * Legacy single-scope selector — returns the highest-precedence granted scope
 * (all > assigned > own), or null. Prefer selectPermissionScopes for new code.
 */
export const selectPermissionScope = (key) => (state) => {
  const scopes = selectPermissionScopes(key)(state);
  if (!scopes) return null;
  if (scopes.includes("all")) return "all";
  if (scopes.includes("assigned")) return "assigned";
  if (scopes.includes("own")) return "own";
  return null;
};

/**
 * True if the permission is granted at any scope. Use this for top-level
 * navigation and Add buttons that don't have a row context.
 *
 * For per-row checks (Edit/Delete on a specific row) prefer the
 * usePermission(key, model) hook, which evaluates scope against model context.
 */
export const selectHasPermission = (key) => (state) => {
  if (selectIsSuperAdmin(state)) return true;
  const perms = state?.user?.permissions || {};
  return Object.prototype.hasOwnProperty.call(perms, key);
};

export const { updateUserProfile, login, setPermissions, logout, setAuthChecked } = userSlice.actions;
export default userSlice.reducer;
